// ===========================================
// konakla - Sentry PII Sanitizer
// ===========================================
//
// Sentry'ye giden eventlerden kullanıcı PII'sini (email, telefon, adres, TC,
// auth header'ları) temizler. Client ve server config ortak kullanır.

// Sentry client ve server `beforeSend` hook'ları farklı event tipleri kullanır
// (ErrorEvent / TransactionEvent / Event). Burada structural bir generic tip
// kullanıyoruz ki sanitizer her iki tarafta da geçerli olsun.

const EMAIL_RE = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
// +90, 0090, 0 ile başlayan 10-11 haneli Türk telefon desenleri.
const PHONE_RE = /(?:\+?90[ -]?)?0?[ -]?5\d{2}[ -]?\d{3}[ -]?\d{2}[ -]?\d{2}/g;
// 11 haneli TC kimlik
const TCKN_RE = /\b[1-9]\d{10}\b/g;
// 10 haneli VKN (vergi kimlik no) — TCKN'den ayrıştırmak için TCKN scrub'ından
// SONRA çalıştırılır (uzun olan önce match etsin).
const VKN_RE = /\b\d{10}\b/g;

function scrubString(value: string): string {
  return value
    .replace(EMAIL_RE, "[email]")
    .replace(PHONE_RE, "[phone]")
    .replace(TCKN_RE, "[tckn]")
    .replace(VKN_RE, "[vkn]");
}

function scrubValue(value: unknown): unknown {
  if (typeof value === "string") return scrubString(value);
  if (Array.isArray(value)) return value.map(scrubValue);
  if (value && typeof value === "object") {
    const result: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      // Hassas header'ları tamamen at.
      if (
        k.toLowerCase() === "authorization" ||
        k.toLowerCase() === "cookie" ||
        k.toLowerCase() === "set-cookie" ||
        k.toLowerCase().includes("token") ||
        k.toLowerCase().includes("secret") ||
        k.toLowerCase().includes("password")
      ) {
        result[k] = "[redacted]";
        continue;
      }
      result[k] = scrubValue(v);
    }
    return result;
  }
  return value;
}

export function beforeSend<T extends Record<string, unknown>>(event: T): T {
  // Any-cast zorunluluğu: Sentry Event / ErrorEvent / TransactionEvent type'ları
  // union olarak "beforeSend" imzasında farklı görünür. Alanların varlığı runtime'da
  // güvenli — yoklarsa hiçbir şey yapmayız.
  const ev = event as Record<string, unknown>;

  if (ev.user && typeof ev.user === "object") {
    const u = ev.user as { id?: string };
    ev.user = { id: u.id };
  }

  const request = ev.request as
    | { headers?: unknown; data?: unknown; query_string?: unknown; url?: unknown }
    | undefined;
  if (request) {
    if (request.headers) request.headers = scrubValue(request.headers);
    if (request.data !== undefined) request.data = scrubValue(request.data);
    if (request.query_string) request.query_string = scrubString(String(request.query_string));
    if (request.url) request.url = scrubString(String(request.url));
  }

  if (Array.isArray(ev.breadcrumbs)) {
    ev.breadcrumbs = (ev.breadcrumbs as Array<{ message?: string; data?: unknown }>).map((bc) => ({
      ...bc,
      message: bc.message ? scrubString(bc.message) : bc.message,
      data: bc.data ? scrubValue(bc.data) : bc.data,
    }));
  }

  const exception = ev.exception as { values?: Array<{ value?: string }> } | undefined;
  if (exception?.values) {
    exception.values = exception.values.map((exItem) => ({
      ...exItem,
      value: exItem.value ? scrubString(exItem.value) : exItem.value,
    }));
  }

  if (typeof ev.message === "string") {
    ev.message = scrubString(ev.message);
  }

  return event;
}
